/**
 * The Hour Optimizer's live panel: the campaign's hour profile with the
 * settings being edited and the extra drops those settings place (one read,
 * `useHourExtras` — the planner's own placement, so the ⚡ marks and the ⚡
 * line move as you edit), composed into the insight, the animated chart,
 * the stats card (hover · click to pin) and the all-hours table. Read-only.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useMemo, useState } from 'react';
import { reductionWords, describeHourFormula, type DropGenHourOptimizerConfig } from '@trafficpulse/shared';

import { getApiErrorMessage } from '@/lib/utils';
import { countryToFlagEmoji } from '@/utils/country-flag-emoji';
import { notEnoughDataValue } from '@/features/drop-generator/components/editor/rules/rule-model';

import { ExtrasLine } from './extras/ExtrasLine';
import type { HourExtrasRead } from './extras/use-hour-extras';
import { chartModel, count, defaultHour, type CountryWindow } from './hour-chart-math';
import { HourNumbersTable } from './HourNumbersTable';
import { HourChartLegend } from './chart/HourChartLegend';
import { HourPanelSkeleton } from './chart/HourPanelSkeleton';
import { HourInsight } from './chart/HourInsight';
import { HourProfileChart } from './chart/HourProfileChart';
import { HourStatsCard } from './HourStatsCard';
import { hasHourTags, HourTagNotes } from './tags';

interface Props {
  /** The window's one read (profile + the extras it places). */
  read: HourExtrasRead;
  countryKey: string;
  campaignWiserId: number | null;
  config: DropGenHourOptimizerConfig;
  startMin: number | null;
  endMin: number | null;
  slotMinutes: number[];
  /** The country's window — shown on the chart and read in the insight. */
  window: CountryWindow | null;
}

export const HourProfilePanel: FC<Props> = (p) => {
  const { profile, preview } = p.read;
  const [hovered, setHovered] = useState<number | null>(null);
  const [pinned, setPinned] = useState<number | null>(null);
  const [showTable, setShowTable] = useState(false);
  // Switched on: the ⚡ the draft's settings place — the same answer the planner will give. Off: none.
  const extras = useMemo(
    () => (p.config.enabled && preview ? [...preview.extras, ...(preview.probe ? [preview.probe] : [])].map((e) => ({ minute: e.minute, kind: e.kind })) : []),
    [p.config.enabled, preview],
  );
  const keptMinutes = p.config.enabled ? preview?.keptRegularMinutes ?? p.slotMinutes : p.slotMinutes;
  // Like the planner: with no end time, its hours end at its last regular drop.
  const spanEnd = p.endMin ?? (p.slotMinutes.length > 0 ? Math.max(...p.slotMinutes) : null);
  const rows = useMemo(
    () => (profile.data ? chartModel(profile.data, { startMin: p.startMin, endMin: spanEnd, slotMinutes: keptMinutes, extras, window: p.window }) : []),
    [profile.data, p.startMin, spanEnd, keptMinutes, extras, p.window],
  );
  if (!p.campaignWiserId) return <Empty text="pick a campaign in step 1 to see its hours" />;
  if (profile.isPending) return <HourPanelSkeleton />;
  if (profile.isError || !profile.data) return <Empty text={`Hour profile unavailable — ${profile.error ? getApiErrorMessage(profile.error).replace(/\.$/, '') : 'no answer yet'}. The optimizer adds nothing until it loads.`} />;
  const data = profile.data;
  const flag = countryToFlagEmoji(p.countryKey);
  const totalVisits = rows.reduce((s, r) => s + r.uniqueVisits, 0);
  const active = pinned ?? hovered ?? defaultHour(rows);
  const pin = (h: number) => setPinned((cur) => (cur === h ? null : h));
  const showMoney = rows.some((r) => r.revenue > 0 || r.cost > 0);
  return (
    <div className="flex flex-col gap-3" key={data.fetchedAt}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">This campaign&apos;s day, hour by hour</span>
        <span className="text-xs text-muted-foreground">
          {data.rankedHours} of 24 hours ranked · {describeHourFormula(data.formula)} · {count(totalVisits)} unique visits
          {profile.isFetching ? ' · updating…' : ''}
        </span>
        <button type="button" onClick={() => setShowTable((v) => !v)} className="ml-auto text-xs font-medium text-primary hover:underline">
          {showTable ? 'Hide the numbers' : 'Show all 24 hours'}
        </button>
      </div>
      {data.rankedHours === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
          {totalVisits === 0
            ? 'No traffic on this campaign in that lookback — there is nothing to rank yet. With the optimizer on, one probe drop a day starts the learning.'
            : allExcludedNote(notEnoughDataValue(p.config.rules))}
        </p>
      ) : null}
      <HourInsight rows={rows} rankedHours={data.rankedHours} startMin={p.startMin} endMin={spanEnd} window={p.window} extras={preview ? <ExtrasLine preview={preview} on={p.config.enabled} /> : null} />
      {p.config.enabled && preview?.reductions?.length ? <div aria-label="Slot reductions" className="rounded border border-warning/30 bg-warning/5 p-2 text-xs">
        {preview.reductions.map((r) => <p key={r.hourStartMin}>{reductionWords(r)}</p>)}
      </div> : null}
      <HourTagNotes profile={data} />
      <HourChartLegend hasRules={rows.some((r) => (r.ruleHits ?? []).some((h) => h.state === 'fired' && h.action !== 'exclude'))} hasContainer={p.startMin !== null} hasTags={hasHourTags(rows)} />
      <HourProfileChart rows={rows} rankedHours={data.rankedHours} startMin={p.startMin} endMin={spanEnd} slotMinutes={keptMinutes} extras={extras} active={active} pinned={pinned} onHover={setHovered} onPin={pin} timezone={data.timezone} countryFlag={flag} window={p.window} tagColors={data.tagColors} />
      <HourStatsCard row={rows[active]} rankedHours={data.rankedHours} formulaLabel={describeHourFormula(data.formula)} pinned={pinned !== null} formula={data.formula} timezone={data.timezone} countryFlag={flag} hasContainer={p.startMin !== null} tagColors={data.tagColors} />
      {showTable ? <HourNumbersTable rows={rows} showMoney={showMoney} active={active} pinned={pinned} onPin={pin} tagColors={data.tagColors} /> : null}
    </div>
  );
};

/** Every hour excluded: say which rule and what to change. */
const allExcludedNote = (floor: number | null) =>
  floor !== null
    ? `Every hour is excluded by "Not enough data" (fewer than ${count(floor)} unique visits) — lower its number in Rules or widen the formula's first range to rank them.`
    : 'Every hour is excluded by a rule — change the Exclude rules or the formula’s ranges to rank them.';

const Empty: FC<{ text: string }> = ({ text }) => (
  <p className="rounded-lg border border-dashed border-border px-4 py-4 text-sm text-muted-foreground">{text}</p>
);
